import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from '../MemberFormModal/MemberFormModal.module.css';

import { GENDER_OPTIONS } from 'constants';
import { participationStatusOptions } from 'config/admin/athlete';
import { addAthlete, updateAthlete, findAthleteMatches } from 'api/userApi';
import { clubForId } from 'utils/clubFeatures';
import {
  GROUPING_BASIS,
  groupingBasisOf,
  YEAR_LEVEL_OPTIONS,
  yearLevelOfGroup,
} from 'utils/groupLabel';
import {
  notifyContactPromotions,
  notifyContactReclassifications,
} from 'utils/promotionToast';

import NameInputBox from 'components/common/NameInputBox';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomSelect from 'components/common/CustomSelect';
import CustomDatePicker from 'components/common/CustomDatePicker';
import LabelText from 'components/common/LabelText';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';

// Display labels only — the `value`s are the API's MembershipType strings ('Full' / 'Guest' / 'Visitor') and must not
// change. An athlete's 'Full' membership reads as "Full Member" in the UI (consistent with the member-role badge).
// Full Member is the only type an admin can give an athlete. The other two are added to the options only for an
// athlete who already has that type (see membershipTypeOptionsFor), so the field shows the real membership and the
// admin can keep it or change the athlete to a Full Member, never the reverse:
//   Guest   is no longer offered: the guests feature is hidden for every club (Doug, 2026-10-11).
//   Visitor is meet-linked: it can only be MINTED by a multi-club meet registration.
export const MEMBERSHIP_TYPE_OPTIONS = [{ value: 'Full', label: 'Full Member' }];

const GUEST_MEMBERSHIP_TYPE_OPTION = { value: 'Guest', label: 'Guest' };
const VISITOR_MEMBERSHIP_TYPE_OPTION = { value: 'Visitor', label: 'Visitor' };

// The membership types offered for an athlete whose type is currently `currentType` (undefined for a new athlete).
// With only Full Member to offer there is nothing to choose, and the forms do not show the field at all.
export const membershipTypeOptionsFor = (currentType) =>
  currentType === 'Guest'
    ? [...MEMBERSHIP_TYPE_OPTIONS, GUEST_MEMBERSHIP_TYPE_OPTION]
    : currentType === 'Visitor'
      ? [...MEMBERSHIP_TYPE_OPTIONS, VISITOR_MEMBERSHIP_TYPE_OPTION]
      : MEMBERSHIP_TYPE_OPTIONS;

// The "guest-class" membership types (Guest + Visitor) share the same edit surface — external club + expiry window,
// with participation status derived server-side from that window. Mirrors the server's MembershipTypes.IsGuestClass;
// use anywhere the form previously special-cased 'Guest' so Visitors get the identical fields.
const isGuestClassType = (type) => type === 'Guest' || type === 'Visitor';

// Confidence label + colour for a possible-duplicate-athlete match (see the server AthleteMatchingService tiers).
const MATCH_TIER = {
  Exact: { label: 'Exact match', color: '#b91c1c' },
  LikelyFuzzyName: { label: 'Likely the same', color: '#b45309' },
  LikelyDobTypo: { label: 'Likely the same — check date of birth', color: '#b45309' },
  SameNameVerify: { label: 'Same name — verify', color: '#6b7280' },
};

// A real, plausible calendar DOB in yyyy-MM-dd: valid year (1900..today), month 1–12, and a day that actually exists
// for that month/year (round-tripped through Date to reject rollovers like 2016-02-31). Gates the duplicate check so
// it doesn't fire on the partial/invalid dates the picker emits while the admin is still typing each part.
const isValidBirthDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!m) return false;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (year < 1900 || year > new Date().getFullYear()) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(year, month - 1, day);
  return (
    d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
  );
};

// What the form is given when the page has no team list for it: a club without teams.
const NO_CLUB_TEAMS = { hasTeams: false, teams: [], teamWords: null };

const AthleteFormModal = ({
  show,
  athlete = null,
  memberEmail,
  memberName,
  clubId,
  onClose,
  onSuccess,
  clubTeams = NO_CLUB_TEAMS,
}) => {
  const isEdit = !!athlete;
  // On edit, exclude the athlete being edited from its own duplicate check (athleteID === AthleteProfileID).
  const excludeAthleteId = athlete?.athleteID;

  // A club that groups by year level asks for the student's Year Level, which the server needs: it never works a
  // year level out from a birth date, because school cut-off dates differ. The date of birth is still required.
  // On edit the field starts at the student's current year, read from their stored group number.
  const club = useSelector((state) => clubForId(state.user, clubId));
  const groupsByYearLevel = groupingBasisOf(club) === GROUPING_BASIS.YEAR_LEVEL;

  // A club that has teams (a school's houses or factions) offers a Team field, named in the club's own word.
  // It is optional: blank is no team. The page that opens the form loads the club's teams once and passes them
  // in (hooks/useClubTeams.js), so opening the form makes no request for them.
  const { hasTeams, teams, teamWords } = clubTeams;
  const teamOptions = teams.map((team) => ({ value: team.teamID, label: team.name }));

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    birthday: '',
    competingAge: '',
    clubAthleteNumber: '',
    participationStatus: 'YES',
    membershipType: 'Full',
    externalClubName: '',
    expiresAt: '',
    yearLevel: '',
    teamId: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Possible existing-athlete duplicates surfaced as the admin enters the identity fields (warn-only).
  const [athleteMatches, setAthleteMatches] = useState([]);

  useEffect(() => {
    if (isEdit && athlete) {
      setFormValues({
        firstName: athlete.firstName || '',
        lastName: athlete.lastName || '',
        gender: athlete.gender || '',
        birthday: athlete.birthDate || '',
        competingAge: athlete.competingAge || '',
        clubAthleteNumber: athlete.clubAthleteNumber || '',
        participationStatus:
          athlete.participationStatus?.toUpperCase() || 'YES', // Convert to uppercase
        membershipType: athlete.membershipType || '',
        externalClubName: athlete.externalClubName || '',
        expiresAt: athlete.expiresAt || '',
        yearLevel: yearLevelOfGroup(athlete.ageGroup),
        teamId: athlete.teamID || '',
      });
    } else {
      // Reset form for new athlete. A new athlete is always a Full Member: it is the only type offered, so the
      // Membership Type field is not shown for one.
      setFormValues({
        firstName: '',
        lastName: '',
        gender: '',
        birthday: '',
        competingAge: '',
        clubAthleteNumber: '',
        participationStatus: 'YES',
        membershipType: 'Full',
        externalClubName: '',
        expiresAt: '',
        yearLevel: '',
        teamId: '',
      });
    }
    setErrors({});
  }, [isEdit, athlete, show]);

  // Duplicate-athlete heads-up: once name + gender + DOB are all present, ask the server for possible matches and
  // warn. Debounced; warn-only — the admin decides. The edited athlete is excluded from its own check. The server
  // shows this club's athletes that share the birth date or this family's email, and an athlete outside the club only
  // when it is in this family (the member's email); never another club's athlete (Doug, 2026-11-05).
  useEffect(() => {
    const first = formValues.firstName.trim();
    const last = formValues.lastName.trim();
    const gender = formValues.gender;
    const birthday = formValues.birthday;
    if (!first || !last || !gender || !birthday) {
      setAthleteMatches([]);
      return undefined;
    }
    // Don't fire while the date is still being typed — the picker pads each part, so intermediate values can be
    // implausible (years 0002→0020→0201, or a month/day not yet valid). Only query once it's a real calendar DOB.
    if (!isValidBirthDate(birthday)) {
      setAthleteMatches([]);
      return undefined;
    }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const resp = await findAthleteMatches(clubId, {
          firstName: first,
          lastName: last,
          gender,
          birthDate: birthday,
          contactEmail: memberEmail || '',
          requireSharedDetail: true,
          excludeAthleteId,
        });
        if (active) setAthleteMatches(resp?.data || []);
      } catch (e) {
        if (active) setAthleteMatches([]);
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [
    formValues.firstName,
    formValues.lastName,
    formValues.gender,
    formValues.birthday,
    clubId,
    memberEmail,
    excludeAthleteId,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formValues.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formValues.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formValues.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formValues.birthday) {
      newErrors.birthday = 'Date of birth is required';
    }

    if (groupsByYearLevel && formValues.yearLevel === '') {
      newErrors.yearLevel = 'Year level is required';
    }

    if (!formValues.participationStatus) {
      newErrors.participationStatus = 'Participation status is required';
    }

    // Club athlete number is required and must be greater than 0
    if (
      !formValues.clubAthleteNumber ||
      parseInt(formValues.clubAthleteNumber) <= 0
    ) {
      newErrors.clubAthleteNumber =
        'Club ID number is required and must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const athleteData = {
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        gender: formValues.gender,
        birthDate: formValues.birthday,
        clubAthleteNumber: formValues.clubAthleteNumber
          ? parseInt(formValues.clubAthleteNumber)
          : null,
        participationStatus: formValues.participationStatus,
        memberEmail: memberEmail,
        clubId,
        membershipType: formValues.membershipType,
        ...(isGuestClassType(formValues.membershipType)
          ? {
              externalClubName: formValues.externalClubName,
              expiresAt: formValues.expiresAt,
            }
          : {}),
        ...(groupsByYearLevel
          ? { yearLevel: parseInt(formValues.yearLevel, 10) }
          : {}),
        // Sent only by a club that has teams. An empty value means no team: on an edit it takes the athlete
        // out of their team. A club without teams sends nothing, so the server leaves the field alone.
        ...(hasTeams ? { teamID: formValues.teamId || '' } : {}),
      };

      if (isEdit) {
        const resp = await updateAthlete(clubId, athlete.athleteID, athleteData);
        toast.success('Athlete profile updated successfully');
        notifyContactPromotions(resp);
        notifyContactReclassifications(resp);
      } else {
        const resp = await addAthlete(athleteData);
        toast.success('Athlete profile added successfully');
        notifyContactPromotions(resp);
        notifyContactReclassifications(resp);
      }

      onSuccess();
    } catch (error) {
      console.error('Athlete form error:', error);

      // Extract error message from the response
      let errorMessage = isEdit
        ? 'Failed to update athlete profile'
        : 'Failed to add athlete profile';

      // Check if error.response exists and has data
      if (error.response) {
        if (error.response.status === 400) {
          // For 400 errors with text response
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data?.Message) {
            // In case the API returns JSON with a message field
            errorMessage = error.response.data.Message;
          } else if (error.response.data?.Error) {
            // In case the API returns JSON with an error field
            errorMessage = error.response.data.Error;
          }
        } else if (error.response.data) {
          // For other error status codes
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data?.Message) {
            errorMessage = error.response.data.Message;
          }
        }
      } else if (error.message) {
        // Network or other errors
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  // Keyed off the original athlete, not the live form value, so an existing Guest's or Visitor's own type stays
  // available across in-form changes. With one option there is nothing to choose and the field is not shown.
  const membershipTypeOptions = membershipTypeOptionsFor(
    athlete?.membershipType
  );
  const showMembershipType = membershipTypeOptions.length > 1;

  return (
    // No overlay click-to-dismiss — closing is deliberate (the × button), so a mis-click doesn't lose edits.
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {isEdit ? 'Edit Athlete Profile' : 'Add Athlete Profile'}
          </h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.memberInfo}>
            <LabelText
              label="Primary Contact:"
              text={memberName}
              labelStyle={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#6b7280',
              }}
              textStyle={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#374151',
              }}
              style={{
                marginBottom: '1.5rem',
                padding: '0.75rem',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            />
          </div>

          <div className={styles.form}>
            <NameInputBox
              label="Athlete Name"
              formValues={formValues}
              errors={errors}
              handleInputChange={handleInputChange}
            />

            <div className="twoColumnRow">
              <div className="flex1">
                <CustomSelect
                  label="Gender"
                  value={formValues.gender}
                  onChange={handleInputChange}
                  name="gender"
                  options={GENDER_OPTIONS}
                  error={errors.gender}
                />
              </div>
              <div className="flex1">
                <CustomDatePicker
                  label="Date of Birth"
                  name="birthday"
                  value={formValues.birthday}
                  onChange={handleInputChange}
                  error={errors.birthday}
                />
              </div>
            </div>

            {(groupsByYearLevel || hasTeams) && (
              <div className="twoColumnRow">
                {groupsByYearLevel && (
                  <div className="flex1">
                    <CustomSelect
                      label="Year Level"
                      value={formValues.yearLevel}
                      onChange={handleInputChange}
                      name="yearLevel"
                      options={YEAR_LEVEL_OPTIONS}
                      error={errors.yearLevel}
                    />
                  </div>
                )}
                {hasTeams && (
                  <div className="flex1">
                    <CustomSelect
                      label={teamWords.team}
                      value={formValues.teamId}
                      onChange={handleInputChange}
                      name="teamId"
                      options={teamOptions}
                    />
                  </div>
                )}
                {!(groupsByYearLevel && hasTeams) && <div className="flex1" />}
              </div>
            )}

            {athleteMatches.length > 0 && (
              <div
                style={{
                  border: '1px solid #fcd34d',
                  background: '#fffbeb',
                  borderRadius: 8,
                  padding: '10px 12px',
                  margin: '0 0 1rem',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color: '#92400e',
                    fontSize: '0.85rem',
                    marginBottom: 6,
                  }}
                >
                  Heads up — {athleteMatches.length} existing athlete
                  {athleteMatches.length === 1 ? '' : 's'} may be the same person. Check before saving.
                </div>
                {athleteMatches.map((m) => {
                  const tier = MATCH_TIER[m.matchTier] || {
                    label: 'Possible match',
                    color: '#6b7280',
                  };
                  return (
                    <div
                      key={m.athleteProfileID}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 10,
                        padding: '6px 8px',
                        background: '#fff',
                        border: '1px solid #fde68a',
                        borderRadius: 6,
                        marginTop: 6,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.85rem' }}>
                          {m.firstName} {m.lastName}
                        </div>
                        <div style={{ fontSize: '0.76rem', color: '#6b7280' }}>
                          {m.birthDate} · {m.gender} ·{' '}
                          {m.primaryContactName
                            ? `${m.primaryContactName}’s family`
                            : 'another family'}
                          {m.isInThisClub ? ' · In your club' : ' · Not in your club'}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          color: tier.color,
                        }}
                      >
                        {tier.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="twoColumnRow">
              <div className="flex1">
                <CustomInput
                  label="Club ID Number (Required)"
                  name="clubAthleteNumber"
                  type="number"
                  value={String(formValues.clubAthleteNumber || '')}
                  placeholder="e.g. 751"
                  onChange={handleInputChange}
                  error={errors.clubAthleteNumber}
                />
              </div>
              {/* Participation Status is admin-controlled only for Full athletes. For guest-class athletes (Guest +
                  Visitor), status is derived server-side from the Membership Expiration Date (future ⇒ Active), so the
                  control is hidden. */}
              {!isGuestClassType(formValues.membershipType) && (
                <div className="flex1">
                  <CustomSelect
                    label="Participation Status"
                    value={formValues.participationStatus}
                    onChange={handleInputChange}
                    name="participationStatus"
                    options={participationStatusOptions}
                    error={errors.participationStatus}
                  />
                </div>
              )}
            </div>
            {showMembershipType && (
              <div
                style={
                  !isGuestClassType(formValues.membershipType)
                    ? { paddingBottom: '4rem' }
                    : {}
                }
              >
                <CustomDropdown
                  label="Membership Type"
                  name="membershipType"
                  value={formValues.membershipType}
                  onChange={handleDropdownChange}
                  options={membershipTypeOptions}
                  error={errors.membershipType}
                />
              </div>
            )}
            {isGuestClassType(formValues.membershipType) && (
              <div className="twoColumnRow">
                <div className="flex1">
                  <CustomInput
                    label="External Club Name"
                    name="externalClubName"
                    type="text"
                    value={String(formValues.externalClubName || '')}
                    placeholder=""
                    onChange={handleInputChange}
                    error={errors.externalClubName}
                  />
                </div>
                <div className="flex1">
                  <CustomDatePicker
                    label="Membership Expiration Date"
                    name="expiresAt"
                    value={formValues.expiresAt}
                    onChange={handleInputChange}
                    error={errors.expiresAt}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Athlete'}
          </button>
        </div>
      </div>
    </div>
  );
};

AthleteFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  athlete: PropTypes.object,
  memberEmail: PropTypes.string.isRequired,
  memberName: PropTypes.string.isRequired,
  clubId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  // The club's teams, from useClubTeams: { hasTeams, teams, teamWords }. Left out for a club without teams.
  clubTeams: PropTypes.shape({
    hasTeams: PropTypes.bool,
    teams: PropTypes.array,
    teamWords: PropTypes.object,
  }),
};

export default AthleteFormModal;
